import { Box } from "@mui/material";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Outlet, useMatch, useNavigate } from "react-router-dom";
import { isActiveUserArtistAtom } from "../../../atoms/user/activeUserAtom";
import { TRACKING_EVENTS_NAME } from "../../../constants/trackingEvents";
import { DevLinkProvider, EngineEarsFooter2024 } from "../../../devlink";
import { useMediaQueryBreakpoint } from "../../../hooks/useMediaQuery";
import { ROUTE_PREFIXES } from "../../../routes";
import { SCREENS } from "../../../routes/screens";
import { getMyMusicRoute } from "../../../store/utils/routeGetters";
import { emitAnalyticsTrackingEvent } from "../../../utils/analyticsUtils";
import { BetaChip } from "../../components/BetaChip/BetaChip";
import { useBottomTabBarOverlayView } from "../../components/Navigation/BottomNav/useBottomTabBarOverlayView";
import {
  BookingsScreenFooterWrapper,
  BookingsScreenHeader,
  StyledBookingTab,
} from "../BookingsScreen/BookingsScreen.styles";
import { CreateReleaseButton } from "../BookingsScreen/components/ReleaseTableView/components/CreateReleaseButton";
import {
  EETabs,
  StyledContainer,
  TabsBottomGreyBar,
} from "../ProfileScreen/ProfileScreen.styles";
import { a11yProps } from "../ProfileScreen/utils";
import { MyMusicTabs, MyMusicTabsMapping } from "./constants";

const standardTabsList = Object.values(MyMusicTabsMapping).reverse();
const artistTabsList = Object.values(MyMusicTabsMapping);

export const MyMusicScreenLayout = () => {
  const match = useMatch(`${ROUTE_PREFIXES.MY_MUSIC}/:tab`);
  const navigate = useNavigate();
  const isUserArtist = useAtomValue(isActiveUserArtistAtom);
  const selectedTab = useMemo(() => {
    return match?.params.tab;
  }, [match]);

  const musicScreenRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    musicScreenRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  const handleTabClick = useCallback(
    (_: React.SyntheticEvent, tabValue: string) => {
      emitAnalyticsTrackingEvent("my_music_tab_clicked", {
        tab: tabValue,
      });
      navigate(getMyMusicRoute(tabValue as MyMusicTabs));
    },
    [navigate],
  );

  const tabsList = useMemo(
    () => (isUserArtist ? artistTabsList : standardTabsList),
    [isUserArtist],
  );
  const isReleasesTabSelected = useMemo(
    () => selectedTab === MyMusicTabs.Releases,
    [selectedTab],
  );
  const { isDesktop } = useMediaQueryBreakpoint();

  const mobileCreateReleaseButton = useBottomTabBarOverlayView(
    !isDesktop,
    <Box
      p={1}
      sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
    >
      <CreateReleaseButton
        fullWidth
        analyticsEvent={{
          name: TRACKING_EVENTS_NAME.ARTIST_CLICKED_CREATE_RELEASE_BUTTON,
          data: { page: SCREENS.BOOKINGS },
        }}
      />
    </Box>,
  );
  return (
    <Box>
      <StyledContainer sx={{ padding: "44px 16px 0 16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Box display="flex" alignItems="center" margin="0 auto" gap={1}>
            <BookingsScreenHeader ref={musicScreenRef}>
              {isReleasesTabSelected ? "My Releases" : "My Music"}
            </BookingsScreenHeader>
            {isReleasesTabSelected && <BetaChip />}
          </Box>
          <Box
            display="flex"
            justifyContent={{ xs: "center", md: "space-between" }}
          >
            <Box
              sx={(theme) => ({
                position: "relative",
                alignSelf: "flex-start",
                maxWidth: "100%",
                [theme.breakpoints.down("sm")]: { alignSelf: "center" },
              })}
            >
              <TabsBottomGreyBar />

              <Box
                id="space-between-spacing"
                display="flex"
                justifyContent="space-between"
                width="100%"
              >
                <EETabs
                  value={selectedTab}
                  onChange={handleTabClick}
                  variant="scrollable"
                  scrollButtons={false}
                >
                  {tabsList.map((tab, index) => {
                    return (
                      <StyledBookingTab
                        disableRipple
                        key={index}
                        value={tab.value}
                        label={tab.label}
                        {...a11yProps(tab.value)}
                      />
                    );
                  })}
                </EETabs>
              </Box>
            </Box>
            {isReleasesTabSelected && isDesktop && (
              <Box display="flex" alignItems="flex-end">
                <CreateReleaseButton
                  analyticsEvent={{
                    name: TRACKING_EVENTS_NAME.ARTIST_CLICKED_CREATE_RELEASE_BUTTON,
                    data: { page: ROUTE_PREFIXES.MY_MUSIC },
                  }}
                />
              </Box>
            )}
          </Box>
          <Outlet />
        </Box>
      </StyledContainer>
      <BookingsScreenFooterWrapper>
        {isReleasesTabSelected && mobileCreateReleaseButton}
        <DevLinkProvider>
          <EngineEarsFooter2024 />
        </DevLinkProvider>
      </BookingsScreenFooterWrapper>
    </Box>
  );
};
